import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  CardMedia,
  LinearProgress
} from '@mui/material';
import {
  Folder,
  AudioFile,
  Home,
  Upload,
  Download,
  Delete,
  Edit,
  Add,
  CreateNewFolder,
  MoreVert,
  PlayArrow,
  Stop,
  VolumeUp,
  ViewList,
  GridView,
  Pause,
  FastForward,
  FastRewind,
  VolumeDown,
  Close
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { usePermissions } from '../hooks/usePermissions';

const MultimediaPage = () => {
  const { multimedia } = useApi();
  const { canAction } = usePermissions();

  // State management
  const [currentType, setCurrentType] = useState('sounds');
  const [currentPath, setCurrentPath] = useState('');
  const [directories, setDirectories] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState([]);

  // View options
  const [viewMode, setViewMode] = useState('grid'); // 'list' or 'grid'

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Audio player modal state
  const [playerDialog, setPlayerDialog] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioVolume, setAudioVolume] = useState(1);
  const [audioLoading, setAudioLoading] = useState(false);

  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [renameDialog, setRenameDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [newName, setNewName] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });

  // Audio player state
  const [audioContext, setAudioContext] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [playing, setPlaying] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification({ ...notification, open: false });
  }, [notification]);

  // Load directory contents
  const loadContents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await multimedia.browse(currentType, currentPath);
      setDirectories(response.directories || []);
      setFiles(response.files || []);
    } catch (error) {
      showNotification('Error cargando contenido: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentType, currentPath, multimedia, showNotification]);

  useEffect(() => {
    loadContents();
  }, [loadContents]);

  useEffect(() => {
    setSelectedPaths([]);
  }, [currentType, currentPath]);

  // Navigation functions
  const navigateToPath = useCallback((newPath) => {
    setCurrentPath(newPath);
  }, []);

  const navigateUp = useCallback(() => {
    if (currentPath) {
      const parentPath = currentPath.split('/').slice(0, -1).join('/');
      setCurrentPath(parentPath);
    }
  }, [currentPath]);

  const getTypeLabel = () => {
    if (currentType === 'sounds') return 'Audios';
    if (currentType === 'moh') return 'Música de Espera';
    if (currentType === 'recordings') return 'Grabaciones';
    return 'Audio';
  };

  const getBreadcrumbs = () => {
    const parts = currentPath ? currentPath.split('/').filter(Boolean) : [];
    const breadcrumbs = [
      { label: getTypeLabel(), path: '' }
    ];

    let buildPath = '';
    parts.forEach(part => {
      buildPath += (buildPath ? '/' : '') + part;
      breadcrumbs.push({ label: part, path: buildPath });
    });

    return breadcrumbs;
  };

  // File operations
  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      showNotification('Selecciona archivos para subir', 'warning');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress({ current: 0, total: uploadFiles.length, fileName: '' });
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        setUploadProgress({ current: i, total: uploadFiles.length, fileName: file.name });

        try {
          await multimedia.upload(currentType, currentPath, file);
          successCount++;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          errorCount++;
          showNotification(`Error subiendo ${file.name}: ${error.message}`, 'error');
        }
      }

      if (successCount > 0) {
        showNotification(
          `${successCount} archivo${successCount > 1 ? 's' : ''} subido${successCount > 1 ? 's' : ''} correctamente${errorCount > 0 ? `, ${errorCount} con error${errorCount > 1 ? 'es' : ''}` : ''}`,
          errorCount > 0 ? 'warning' : 'success'
        );
      }

      setUploadDialog(false);
      setUploadFiles([]);
      setUploadProgress({ current: 0, total: 0, fileName: '' });
      loadContents();
    } catch (error) {
      showNotification('Error subiendo archivos: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showNotification('Ingresa un nombre para la carpeta', 'warning');
      return;
    }

    try {
      setLoading(true);
      await multimedia.createFolder(currentType, currentPath, newFolderName);
      showNotification('Carpeta creada correctamente', 'success');
      setCreateFolderDialog(false);
      setNewFolderName('');
      loadContents();
    } catch (error) {
      showNotification('Error creando carpeta: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) {
      showNotification('Ingresa un nuevo nombre', 'warning');
      return;
    }

    try {
      setLoading(true);
      const itemPath = currentPath ? `${currentPath}/${selectedItem.name}` : selectedItem.name;
      await multimedia.rename(currentType, itemPath, newName);
      showNotification('Elemento renombrado correctamente', 'success');
      setRenameDialog(false);
      setNewName('');
      setSelectedItem(null);
      loadContents();
    } catch (error) {
      showNotification('Error renombrando: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const itemPath = currentPath ? `${currentPath}/${selectedItem.name}` : selectedItem.name;
      await multimedia.delete(currentType, itemPath);
      showNotification('Elemento eliminado correctamente', 'success');
      setDeleteDialog(false);
      setSelectedItem(null);
      loadContents();
    } catch (error) {
      showNotification('Error eliminando: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      const itemPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      await multimedia.download(currentType, itemPath);
      showNotification('Archivo descargado', 'success');
    } catch (error) {
      showNotification('Error descargando archivo: ' + error.message, 'error');
    }
  };

  const buildItemPath = (item) => (currentPath ? `${currentPath}/${item.name}` : item.name);

  const toggleSelection = (item) => {
    const itemPath = buildItemPath(item);
    setSelectedPaths((prev) => (
      prev.includes(itemPath)
        ? prev.filter((p) => p !== itemPath)
        : [...prev, itemPath]
    ));
  };

  const handleDownloadTar = async (paths, name) => {
    try {
      if (!paths?.length) {
        showNotification('Selecciona archivos o carpetas', 'warning');
        return;
      }
      setDownloading(true);
      showNotification('Preparando descarga...', 'info');
      await multimedia.downloadZip(currentType, paths, name || `${currentType}.tar.gz`);
      showNotification('Archivo TAR descargado', 'success');
      setSelectedPaths([]);
    } catch (error) {
      showNotification('Error descargando TAR: ' + error.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  // Context menu functions
  const handleContextMenu = useCallback((event, item) => {
    event.preventDefault();
    setSelectedItem(item);
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Audio playback functions
  const playAudio = async (file) => {
    try {
      setAudioLoading(true);

      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
        setPlaying(false);
      }

      // Mostrar modal inmediatamente con el track info
      setPlayerDialog(true);
      setCurrentTrack({ ...file, path: currentPath ? `${currentPath}/${file.name}` : file.name });

      const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;
      const audioBlob = await multimedia.download(currentType, filePath, true);
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);

      // Setup audio event listeners
      audio.onloadedmetadata = () => {
        setAudioDuration(audio.duration);
        setAudioLoading(false);
      };

      audio.ontimeupdate = () => {
        setAudioProgress(audio.currentTime);
      };

      audio.onended = () => {
        setPlaying(false);
        setPlayerDialog(false);
        setCurrentAudio(null);
        setCurrentTrack(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setAudioLoading(false);
        showNotification('Error al cargar el archivo de audio', 'error');
      };

      // Set volume
      audio.volume = audioVolume;

      setCurrentAudio(audio);
      setCurrentTrack({ ...file, url: audioUrl, path: filePath });
      audio.play();
      setPlaying(true);
    } catch (error) {
      setAudioLoading(false);
      showNotification('Error reproduciendo audio: ' + error.message, 'error');
    }
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
      setPlaying(false);
      setPlayerDialog(false);
      setCurrentTrack(null);
    }
  };

  const togglePlayPause = () => {
    if (currentAudio) {
      if (playing) {
        currentAudio.pause();
        setPlaying(false);
      } else {
        currentAudio.play();
        setPlaying(true);
      }
    }
  };

  const seekAudio = (newTime) => {
    if (currentAudio) {
      currentAudio.currentTime = newTime;
      setAudioProgress(newTime);
    }
  };

  const changeVolume = (newVolume) => {
    setAudioVolume(newVolume);
    if (currentAudio) {
      currentAudio.volume = newVolume;
    }
  };

  const seekForward = () => {
    if (currentAudio) {
      const newTime = Math.min(currentAudio.currentTime + 10, audioDuration);
      seekAudio(newTime);
    }
  };

  const seekBackward = () => {
    if (currentAudio) {
      const newTime = Math.max(currentAudio.currentTime - 10, 0);
      seekAudio(newTime);
    }
  };

  const handleProgressBarClick = (event) => {
    if (currentAudio && audioDuration > 0) {
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const totalWidth = rect.width;
      const percentage = clickX / totalWidth;
      const newTime = percentage * audioDuration;
      seekAudio(newTime);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, item) => {
    event.stopPropagation();
    setSelectedItem(item);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();

    switch (action) {
      case 'rename':
        setNewName(selectedItem.name);
        setRenameDialog(true);
        break;
      case 'delete':
        setDeleteDialog(true);
        break;
      case 'download':
        handleDownload(selectedItem);
        break;
      default:
        break;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file, size = 24) => {
    return <AudioFile color="secondary" sx={{ fontSize: size }} />;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render List View
  const renderListView = () => (
    <List>
      {/* Parent directory link */}
      {currentPath && (
        <ListItem button onClick={navigateUp}>
          <ListItemIcon>
            <Folder />
          </ListItemIcon>
          <ListItemText primary=".." secondary="Directorio padre" />
        </ListItem>
      )}

      {/* Directories */}
      {directories.map((dir, index) => (
        <ListItem
          key={`dir-${index}`}
          button
          onClick={() => navigateToPath(currentPath ? `${currentPath}/${dir.name}` : dir.name)}
          onContextMenu={(e) => handleContextMenu(e, dir)}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
            <Checkbox
              edge="start"
              checked={selectedPaths.includes(buildItemPath(dir))}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(dir);
              }}
            />
            <Folder color="primary" />
          </ListItemIcon>
          <ListItemText
            primary={dir.name}
            secondary="Carpeta"
          />
          <ListItemSecondaryAction>
            <IconButton onClick={(e) => handleMenuOpen(e, dir)}>
              <MoreVert />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}

      {/* Files */}
      {files.map((file, index) => (
        <ListItem
          key={`file-${index}`}
          onContextMenu={(e) => handleContextMenu(e, file)}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
            <Checkbox
              edge="start"
              checked={selectedPaths.includes(buildItemPath(file))}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(file);
              }}
            />
            {getFileIcon(file)}
          </ListItemIcon>
          <ListItemText
            primary={file.name}
            secondary={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip size="small" label={file.extension} />
                <Typography variant="caption">{formatFileSize(file.size)}</Typography>
              </Box>
            }
          />
          <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => playAudio(file)}
              color="primary"
            >
              <PlayArrow />
            </IconButton>
            <IconButton onClick={(e) => handleMenuOpen(e, file)}>
              <MoreVert />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}

      {directories.length === 0 && files.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            No hay archivos o carpetas en este directorio
          </Typography>
        </Box>
      )}
    </List>
  );

  // Render Grid View
  const renderGridView = () => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {/* Parent directory link */}
      {currentPath && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}
            onClick={navigateUp}
          >
            <Folder sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" noWrap>
              ..
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Directorio padre
            </Typography>
          </Paper>
        </Grid>
      )}

      {/* Directories */}
      {directories.map((dir, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`dir-${index}`}>
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}
            onClick={() => navigateToPath(currentPath ? `${currentPath}/${dir.name}` : dir.name)}
            onContextMenu={(e) => handleContextMenu(e, dir)}
          >
            <IconButton
              sx={{ position: 'absolute', top: 4, right: 4 }}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, dir);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Checkbox
              size="small"
              sx={{ position: 'absolute', top: 4, left: 4 }}
              checked={selectedPaths.includes(buildItemPath(dir))}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(dir);
              }}
            />

            <Folder sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" noWrap title={dir.name}>
              {dir.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Carpeta
            </Typography>
          </Paper>
        </Grid>
      ))}

      {/* Files */}
      {files.map((file, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`file-${index}`}>
          <Paper
            sx={{
              p: 2,
              textAlign: 'center',
              position: 'relative',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}
            onContextMenu={(e) => handleContextMenu(e, file)}
          >
            <IconButton
              sx={{ position: 'absolute', top: 4, right: 4 }}
              size="small"
              onClick={(e) => handleMenuOpen(e, file)}
            >
              <MoreVert fontSize="small" />
            </IconButton>

            <IconButton
              sx={{ position: 'absolute', top: 4, left: 4 }}
              size="small"
              color="primary"
              onClick={() => playAudio(file)}
            >
              <PlayArrow fontSize="small" />
            </IconButton>
            <Checkbox
              size="small"
              sx={{ position: 'absolute', top: 4, left: 36 }}
              checked={selectedPaths.includes(buildItemPath(file))}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(file);
              }}
            />

            {getFileIcon(file, 40)}
            <Typography variant="body2" noWrap title={file.name} sx={{ mt: 1 }}>
              {file.name}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
              <Chip size="small" label={file.extension} />
            </Box>
            <Typography variant="caption" color="textSecondary">
              {formatFileSize(file.size)}
            </Typography>
          </Paper>
        </Grid>
      ))}

      {directories.length === 0 && files.length === 0 && (
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              No hay archivos o carpetas en este directorio
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Multimedia de Asterisk
      </Typography>

      <Grid container spacing={3}>
        {/* Type Selection */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Seleccionar Tipo de Audio
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={currentType === 'sounds' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setCurrentType('sounds');
                    setCurrentPath('');
                  }}
                  startIcon={<AudioFile />}
                >
                  Audios del Sistema
                </Button>
                <Button
                  variant={currentType === 'moh' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setCurrentType('moh');
                    setCurrentPath('');
                  }}
                  startIcon={<VolumeUp />}
                >
                  Música de Espera
                </Button>
                <Button
                  variant={currentType === 'recordings' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setCurrentType('recordings');
                    setCurrentPath('');
                  }}
                  startIcon={<AudioFile />}
                >
                  Grabaciones
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Navigation */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs>
                  {getBreadcrumbs().map((crumb, index) => (
                    <Link
                      key={index}
                      component="button"
                      underline="hover"
                      onClick={() => navigateToPath(crumb.path)}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {index === 0 && <Home sx={{ mr: 0.5 }} fontSize="inherit" />}
                      {crumb.label}
                    </Link>
                  ))}
                </Breadcrumbs>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* View Toggle */}
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                  >
                    <ToggleButton value="list">
                      <ViewList />
                    </ToggleButton>
                    <ToggleButton value="grid">
                      <GridView />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedPaths.length > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownloadTar(selectedPaths, `${currentType}.tar.gz`)}
                        disabled={downloading}
                      >
                        {downloading ? 'Preparando...' : `Descargar TAR (${selectedPaths.length})`}
                      </Button>
                    )}
                    {canAction('upload_multimedia') && <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={() => setUploadDialog(true)}
                    >
                      Subir Archivo
                    </Button>}
                    {canAction('create_folder_multimedia') && <Button
                      variant="outlined"
                      startIcon={<CreateNewFolder />}
                      onClick={() => setCreateFolderDialog(true)}
                    >
                      Nueva Carpeta
                    </Button>}
                  </Box>
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                viewMode === 'list' ? renderListView() : renderGridView()
              )}
              {downloading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary">
                    Generando descarga...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Audio player control */}
      {playing && (
        <Fab
          color="secondary"
          aria-label="stop"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={stopAudio}
        >
          <Stop />
        </Fab>
      )}

      {/* Context Menu (3 dots) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canAction('rename_multimedia') && <MenuItem onClick={() => handleMenuAction('rename')}>
          <Edit sx={{ mr: 1 }} />
          Renombrar
        </MenuItem>}
        {selectedItem?.type === 'file' && (
          <MenuItem onClick={() => handleMenuAction('download')}>
            <Download sx={{ mr: 1 }} />
            Descargar
          </MenuItem>
        )}
        {selectedItem?.type === 'directory' && (
          <MenuItem
            onClick={() => handleDownloadTar([buildItemPath(selectedItem)], `${selectedItem.name}.tar.gz`)}
            disabled={downloading}
          >
            <Download sx={{ mr: 1 }} />
            Descargar TAR
          </MenuItem>
        )}
        {canAction('delete_multimedia') && <MenuItem onClick={() => handleMenuAction('delete')}>
          <Delete sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>}
      </Menu>

      {/* Right Click Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {selectedItem?.type === 'file' && (
          <MenuItem onClick={() => {
            playAudio(selectedItem);
            closeContextMenu();
          }}>
            <PlayArrow sx={{ mr: 1 }} />
            Reproducir
          </MenuItem>
        )}
        {canAction('rename_multimedia') && <MenuItem onClick={() => {
          setNewName(selectedItem?.name || '');
          setRenameDialog(true);
          closeContextMenu();
        }}>
          <Edit sx={{ mr: 1 }} />
          Renombrar
        </MenuItem>}
        {selectedItem?.type === 'file' && (
          <MenuItem onClick={() => {
            handleDownload(selectedItem);
            closeContextMenu();
          }}>
            <Download sx={{ mr: 1 }} />
            Descargar
          </MenuItem>
        )}
        {selectedItem?.type === 'directory' && (
          <MenuItem
            onClick={() => {
              handleDownloadTar([buildItemPath(selectedItem)], `${selectedItem.name}.tar.gz`);
              closeContextMenu();
            }}
            disabled={downloading}
          >
            <Download sx={{ mr: 1 }} />
            Descargar TAR
          </MenuItem>
        )}
        {canAction('delete_multimedia') && <MenuItem onClick={() => {
          setDeleteDialog(true);
          closeContextMenu();
        }}>
          <Delete sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>}
      </Menu>

      {/* Audio Player Modal */}
      <Dialog
        open={playerDialog}
        onClose={() => setPlayerDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Reproductor de Audio
            </Typography>
            <IconButton onClick={() => setPlayerDialog(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center' }}>
          {currentTrack && (
            <>
              {/* Track Info */}
              <Box sx={{ mb: 3 }}>
                {audioLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress sx={{ color: 'white', mb: 2 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Cargando audio...
                    </Typography>
                  </Box>
                ) : (
                  <AudioFile sx={{ fontSize: 80, color: 'white', opacity: 0.8, mb: 1 }} />
                )}
                <Typography variant="h6" sx={{ color: 'white', mb: 0.5 }}>
                  {currentTrack.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {currentTrack.extension && `${currentTrack.extension} • `}{formatFileSize(currentTrack.size)}
                </Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {formatTime(audioProgress)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {formatTime(audioDuration)}
                  </Typography>
                </Box>
                <Box
                  onClick={handleProgressBarClick}
                  sx={{
                    cursor: audioLoading ? 'not-allowed' : 'pointer',
                    opacity: audioLoading ? 0.5 : 1,
                    pointerEvents: audioLoading ? 'none' : 'auto'
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={(audioProgress / audioDuration) * 100 || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'white',
                        borderRadius: 4
                      },
                      '&:hover': {
                        transform: 'scaleY(1.2)',
                        transition: 'transform 0.2s ease'
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Control Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                <IconButton
                  onClick={seekBackward}
                  disabled={audioLoading || !currentAudio}
                  sx={{ color: 'white' }}
                >
                  <FastRewind />
                </IconButton>

                <IconButton
                  onClick={togglePlayPause}
                  disabled={audioLoading || !currentAudio}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                    '&:disabled': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      color: 'rgba(255,255,255,0.5)'
                    }
                  }}
                  size="large"
                >
                  {audioLoading ? (
                    <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  ) : (
                    playing ? <Pause /> : <PlayArrow />
                  )}
                </IconButton>

                <IconButton
                  onClick={seekForward}
                  disabled={audioLoading || !currentAudio}
                  sx={{ color: 'white' }}
                >
                  <FastForward />
                </IconButton>
              </Box>

              {/* Volume Control */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                <VolumeDown sx={{ color: 'rgba(255,255,255,0.7)' }} />
                <Box sx={{ width: 100 }}>
                  <LinearProgress
                    variant="determinate"
                    value={audioVolume * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      cursor: 'pointer',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'white',
                        borderRadius: 3
                      }
                    }}
                    onClick={(e) => {
                      const rect = e.target.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const width = rect.width;
                      const newVolume = Math.max(0, Math.min(1, x / width));
                      changeVolume(newVolume);
                    }}
                  />
                </Box>
                <VolumeUp sx={{ color: 'rgba(255,255,255,0.7)' }} />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => !loading && setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Upload sx={{ mr: 1 }} />
            Subir Archivos de Audio
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Box sx={{
                position: 'relative',
                display: 'inline-flex',
                mb: 3
              }}>
                <CircularProgress
                  size={80}
                  thickness={4}
                  sx={{
                    color: '#667eea',
                    animationDuration: '1.5s'
                  }}
                />
                <Box sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <Upload sx={{ color: '#667eea', fontSize: 32 }} />
                </Box>
              </Box>

              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: '#333' }}>
                Subiendo archivos
              </Typography>

              <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                p: 2,
                mt: 2,
                mb: 2
              }}>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{
                    color: '#667eea',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {uploadProgress.fileName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {uploadProgress.current + 1} de {uploadProgress.total} archivo{uploadProgress.total > 1 ? 's' : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
                {[...Array(Math.min(uploadProgress.total, 5))].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 40,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: i < uploadProgress.current + 1 ? '#667eea' : '#e0e0e0',
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </Box>

              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 3 }}>
                Por favor espera mientras se suben los archivos...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Box
                sx={{
                  border: '2px dashed #667eea',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#f8f9ff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#f0f2ff',
                    borderColor: '#764ba2'
                  }
                }}
              >
                <input
                  type="file"
                  multiple
                  accept="audio/*"
                  onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                />
                <label htmlFor="file-upload-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                  <Upload sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
                  <Typography variant="body1" fontWeight="500" sx={{ color: '#667eea' }}>
                    Click para seleccionar archivos
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    o arrastra y suelta aquí
                  </Typography>
                </label>
              </Box>

              {uploadFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1, color: '#333' }}>
                    Archivos seleccionados ({uploadFiles.length})
                  </Typography>
                  <Box sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    p: 1.5
                  }}>
                    {uploadFiles.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'white',
                          borderRadius: 1,
                          p: 1,
                          mb: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <AudioFile sx={{ color: '#667eea', mr: 1 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setUploadDialog(false)}
            disabled={loading}
            sx={{ color: '#666' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={loading || uploadFiles.length === 0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8a 100%)',
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            {loading ? 'Subiendo...' : 'Subir Archivos'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)}>
        <DialogTitle>Crear Nueva Carpeta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la carpeta"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateFolder} variant="contained">Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialog} onClose={() => setRenameDialog(false)}>
        <DialogTitle>Renombrar</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nuevo nombre"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog(false)}>Cancelar</Button>
          <Button onClick={handleRename} variant="contained">Renombrar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar "{selectedItem?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={hideNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MultimediaPage;
